import { PolygonThemeSetting, RGBA } from '@bpd-library/types';

const MIN_ZOOM = 1;
const MAX_ZOOM = 24;
const LINE_WIDTH = 2;
const TRANSITION_ZOOM_MARGIN = 0.75;

const NORMAL_FEATURE_STATE: mapboxgl.Expression = [
    'case',
    ['boolean', ['feature-state', 'hover'], false],
    0,
    ['boolean', ['feature-state', 'focus'], false],
    0,
    1,
];

const HOVER_FEATURE_STATE: mapboxgl.Expression = [
    'case',
    ['boolean', ['feature-state', 'hover'], false],
    1,
    ['boolean', ['feature-state', 'focus'], false],
    1,
    0,
];

const RGBAString = ([r, g, b, a]: RGBA, overwriteAlpha?: number) => {
    a = overwriteAlpha !== undefined ? overwriteAlpha : a;

    return `rgba(${r},${g},${b},${a})`;
};

const layer = (
    map: mapboxgl.Map,
    { variant, normal, hover, minzoom, maxzoom, visibility = 'visible' }: PolygonThemeSetting,
) => {
    // Calculate the minzoom and maxzoom when it's set. This makes sure the layer gets disabled when it's invisible.
    let layerMinzoom = minzoom ? minzoom - TRANSITION_ZOOM_MARGIN : MIN_ZOOM;
    let layerMaxzoom = maxzoom ? maxzoom + TRANSITION_ZOOM_MARGIN : MAX_ZOOM;

    // Limit the minzoom to 1 and the maxzoom to 24.
    if (layerMinzoom < 1) layerMinzoom = 1;
    if (layerMaxzoom > 24) layerMaxzoom = 24;

    if (normal.fill) {
        map.addLayer({
            id: `${variant}-fill`,
            type: 'fill',
            source: variant,
            minzoom: layerMinzoom,
            maxzoom: layerMaxzoom,
            layout: {
                visibility: visibility,
            },
            paint: {
                'fill-color': RGBAString(normal.fill),
                'fill-opacity': NORMAL_FEATURE_STATE,
            },
        });

        setTransitionPaintProperty(`${variant}-fill`, normal.fill, 'fill-color');
    }

    // Only add stroke layer, if it is set
    if (normal.stroke) {
        map.addLayer({
            id: `${variant}-line`,
            type: 'line',
            source: variant,
            minzoom: layerMinzoom,
            maxzoom: layerMaxzoom,
            layout: {
                visibility: 'visible',
            },
            paint: {
                'line-color': RGBAString(normal.stroke),
                'line-width': LINE_WIDTH,
                'line-opacity': NORMAL_FEATURE_STATE,
            },
        });

        setTransitionPaintProperty(`${variant}-line`, normal.stroke, 'line-color');
    }

    if (hover) {
        map.addLayer({
            id: `${variant}-fill-hover`,
            type: 'fill',
            source: variant,
            minzoom: layerMinzoom,
            maxzoom: layerMaxzoom,
            layout: {
                // Make the layer visible by default.
                visibility: visibility,
            },
            paint: {
                'fill-color': RGBAString(hover.fill),
                'fill-opacity': HOVER_FEATURE_STATE,
            },
        });

        setTransitionPaintProperty(`${variant}-fill-hover`, hover.fill, 'fill-color');

        if (normal.stroke) {
            map.addLayer({
                id: `${variant}-line-hover`,
                type: 'line',
                source: variant,
                minzoom: layerMinzoom,
                maxzoom: layerMaxzoom,
                paint: {
                    'line-color': RGBAString(hover.stroke),
                    'line-width': LINE_WIDTH,
                    'line-opacity': HOVER_FEATURE_STATE,
                },
            });

            setTransitionPaintProperty(`${variant}-line-hover`, hover.stroke, 'line-color');
        }
    }

    /**
     * @description Creates a fade-in effect when zooming in or out, based on a minzoom and maxzoom
     * @param {string} layer
     * @param {RGBA} color
     * @param {string} property
     * @return {*}
     */
    function setTransitionPaintProperty(layer: string, color: RGBA, property: string) {
        if (!minzoom || !maxzoom) return;

        map.setPaintProperty(layer, property, [
            'interpolate',
            ['exponential', TRANSITION_ZOOM_MARGIN],
            ['zoom'],
            minzoom - TRANSITION_ZOOM_MARGIN,
            RGBAString(color, 0),
            minzoom,
            RGBAString(color),
            maxzoom,
            RGBAString(color),
            maxzoom + TRANSITION_ZOOM_MARGIN,
            RGBAString(color, 0),
        ]);
    }
};

export default layer;
